import React, { useState, useMemo, useEffect, useCallback } from "react";
import { AnalysisResult } from "../../types";
import { useGuitarMatch } from "../../context/GuitarMatchContext";
import {
  getVisibleTabsForLevel,
  MASTER_METRICS,
} from "../../services/viewPolicy";
import { TabRouter } from "./TabRouter";
import { InitialReport } from "./InitialReport";
import { TRIAL_LIMITS } from "../../services/config";
import { PremiumPaywall } from "../PremiumPaywall";
import { AIOverlay } from "./AIOverlay";
import { NeuralScanner } from "./NeuralScanner";
import { CalibrationResult } from "../assessment/CalibrationResult";
import { toUIModel, UIConsumptionModel } from "../../policy/integrityProtocol";
import { getLockReason, LockReason } from "../../policy/uiDataPolicy";
import { METRIC_GOVERNANCE_POLICY } from "../../services/tabUnlockPolicy";

type PrescriptivePrimary = {
  motor_action_id: string;
  domain: string;
  priority?: number;
  title: string;
  instruction: string;
  steps: string[];
  duration_s: number;
  force_mode: boolean;
  recheck_signal_id: string;
};

type PrescriptiveExplanation = {
  signal_id: string;
  domain: string;
  title: string;
  what_is_measured: string;
  why_it_matters: string;
  severity: "CRITICAL" | "WARNING" | "SUCCESS";
};

function lockReasonToMessage(
  tabId: string,
  reason: LockReason,
  isFr: boolean
): string {
  if (!reason) return "";

  if (reason === "LEVEL_LOCKED") {
    const p: any = (METRIC_GOVERNANCE_POLICY as any)?.[tabId];
    const min = p?.minLevel ?? "?";
    return isFr ? `Niveau requis : ${min}` : `Required level: ${min}`;
  }

  if (reason === "CALIBRATION_REQUIRED") {
    return isFr ? "Calibration requise" : "Calibration required";
  }

  if (reason === "MISSING_DSP_METRICS") {
    return isFr
      ? "Preuve DSP absente (NO=100%)."
      : "Missing DSP proof (NO=100%).";
  }

  return String(reason);
}

export const SmartTrainer: React.FC<{
  result: AnalysisResult;
  onReAnalyze: () => void;
  onHome?: () => void;
  currentLevelId: number;
  lang: "en" | "fr";
}> = ({ result: rawResult, onReAnalyze, onHome, currentLevelId, lang }) => {
  const {
    analysisCount,
    isPremium,
    setActiveOverlay,
    isCoachTransition,
    coachTransitionLabel,
    matchType,
  } = useGuitarMatch();

  const [activeTab, setActiveTab] = useState<string>("report");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [uiModel, setUiModel] = useState<UIConsumptionModel | null>(null);
  const isFr = lang === "fr";

  // ✅ mesure active (cartes / anatomie spectrale)
  const [activeMeasure, setActiveMeasure] = useState<number>(0);

  // ✅ Mode prescriptif : état local
  const [prescriptionAccepted, setPrescriptionAccepted] =
    useState<boolean>(false);
  const [timerLeft, setTimerLeft] = useState<number>(0);

  // -----------------------------
  // 1) Build UI model (async)
  // -----------------------------
  useEffect(() => {
    let mounted = true;
    toUIModel(rawResult).then((m) => {
      if (mounted) setUiModel(m);
    });
    return () => {
      mounted = false;
    };
  }, [rawResult]);

  // Reset prescription state when new analysis arrives
  useEffect(() => {
    setPrescriptionAccepted(false);
    setTimerLeft(0);
  }, [uiModel?.audit_trail?.analysis_hash]);

  // ✅ Reset active measure when tab changes OR analysis changes (hash = stable)
  useEffect(() => {
    setActiveMeasure(0);
  }, [activeTab, uiModel?.audit_trail?.analysis_hash]);

  // Timer tick
  useEffect(() => {
    if (timerLeft <= 0) return;
    const t = setInterval(() => setTimerLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [timerLeft]);

  // -----------------------------
  // 2) Derivations (SAFE when uiModel is null)
  // -----------------------------
  const isVisionLocked = !isPremium && analysisCount > TRIAL_LIMITS.VISION_LOCK_COUNT;

  const visibleTabsList = useMemo(() => {
    const allowedIdsByLevel = getVisibleTabsForLevel(
      currentLevelId,
      analysisCount,
      isPremium
    );

    const tabs: {
      id: string;
      label: string;
      icon: string;
      isPremiumOnly?: boolean;
      dataLocked?: boolean;
      lockReason?: string;
      lockReasonCode?: LockReason;
    }[] = [{ id: "report", label: "Match Verdict", icon: "📊", dataLocked: false }];

    MASTER_METRICS.forEach((m) => {
      const isAllowedByPolicy = allowedIdsByLevel.includes(m.id);
      if (!isAllowedByPolicy) return;

      const reason: LockReason = uiModel
        ? getLockReason(m.id, uiModel as any)
        : "MISSING_DSP_METRICS";

      tabs.push({
        id: m.id,
        label: isFr ? m.label.fr : m.label.en,
        icon: m.icon,
        isPremiumOnly: ["posture", "synchronization", "audio"].includes(m.id),
        dataLocked: Boolean(reason),
        lockReason: lockReasonToMessage(m.id, reason, isFr),
        lockReasonCode: reason,
      });
    });

    return tabs;
  }, [currentLevelId, isFr, analysisCount, isPremium, uiModel]);

  const primaryPrescription = useMemo(() => {
    if (!uiModel) return undefined;
    return (
      ((uiModel.feedback as any)?.primaryPrescription as
        | PrescriptivePrimary
        | undefined) ?? undefined
    );
  }, [uiModel]);

  const explanations = useMemo(() => {
    if (!uiModel) return [];
    return (((uiModel.feedback as any)?.explanations as
      | PrescriptiveExplanation[]
      | undefined) ?? []) as PrescriptiveExplanation[];
  }, [uiModel]);

  const isForcedMode = useMemo(() => {
    return Boolean(primaryPrescription?.force_mode) && !prescriptionAccepted;
  }, [primaryPrescription?.force_mode, prescriptionAccepted]);

  // ✅ IMPORTANT : ce hook DOIT exister même quand uiModel est null
  useEffect(() => {
    if (!uiModel) return;
    if (isForcedMode && activeTab !== "report") setActiveTab("report");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiModel, isForcedMode]);

  // Bandeaux (guard uiModel)
  const isSealBroken = uiModel ? !uiModel.__isSealValid : false;
  const isIntegrityFail = uiModel
    ? uiModel.session.truth_report.integrity_status === "FAIL"
    : false;
  const isLive = uiModel
    ? uiModel.session.truth_report.tech_pack.origin === "REAL_AUDIO"
    : false;
  const showCalibWarning = uiModel
    ? isLive && uiModel.session.truth_report.calibration_status !== "VALID"
    : false;

  // -----------------------------
  // 3) Actions
  // -----------------------------
  const startPrescriptionTimer = useCallback(() => {
    const d = primaryPrescription?.duration_s ?? 30;
    setTimerLeft(d);
  }, [primaryPrescription?.duration_s]);

  const acceptPrescription = useCallback(() => {
    setPrescriptionAccepted(true);
    setTimerLeft(0);
  }, []);

  const recheckNow = useCallback(() => {
    setPrescriptionAccepted(true);
    setTimerLeft(0);
    onReAnalyze();
  }, [onReAnalyze]);

  const renderForcedOverlay = useCallback(() => {
    if (!primaryPrescription) return null;

    const relatedExplanation =
      explanations.find((e) => e.signal_id === primaryPrescription.recheck_signal_id) ||
      explanations.find((e) => e.severity === "CRITICAL") ||
      explanations.find((e) => e.severity === "WARNING") ||
      explanations[0];

    return (
      <div className="absolute inset-0 z-[9998] bg-black/80 backdrop-blur-2xl flex items-center justify-center p-6">
        <div className="w-full max-w-3xl rounded-[32px] border border-white/10 bg-[#05070a]/90 shadow-2xl overflow-hidden">
          <div className="p-8 border-b border-white/10">
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-smart-gold/90">
                  {isFr ? "MODE PRESCRIPTIF" : "PRESCRIPTIVE MODE"}
                </div>
                <h2 className="mt-2 text-2xl md:text-3xl font-black text-white">
                  STOP. {primaryPrescription.title}
                </h2>
                <p className="mt-2 text-slate-300 text-sm leading-relaxed">
                  {primaryPrescription.instruction}
                </p>
              </div>

              <div className="shrink-0 text-right">
                <div className="text-[9px] font-black uppercase text-slate-400">
                  {isFr ? "Durée" : "Duration"}
                </div>
                <div className="text-3xl font-black text-white tabular-nums">
                  {timerLeft > 0
                    ? `${timerLeft}s`
                    : `${primaryPrescription.duration_s}s`}
                </div>
              </div>
            </div>
          </div>

          {relatedExplanation && (
            <div className="p-8 border-b border-white/10">
              <div className="text-[10px] font-black uppercase tracking-widest text-cyan-300/90">
                {isFr ? "Ce que l’IA mesure" : "What the AI measures"}
              </div>
              <p className="mt-2 text-slate-200 text-sm leading-relaxed">
                {relatedExplanation.what_is_measured}
              </p>

              <div className="mt-5 text-[10px] font-black uppercase tracking-widest text-cyan-300/90">
                {isFr ? "Pourquoi c’est important" : "Why it matters"}
              </div>
              <p className="mt-2 text-slate-200 text-sm leading-relaxed">
                {relatedExplanation.why_it_matters}
              </p>

              <div className="mt-4 text-[10px] font-black uppercase text-slate-400">
                {isFr ? "Signal" : "Signal"} :{" "}
                <span className="text-slate-300">
                  {relatedExplanation.signal_id}
                </span>
              </div>
            </div>
          )}

          <div className="p-8">
            <div className="text-[10px] font-black uppercase tracking-widest text-smart-gold/90">
              {isFr ? "Exercice unique (obligatoire)" : "Single mandatory exercise"}
            </div>

            <ul className="mt-4 space-y-3">
              {(primaryPrescription.steps || []).slice(0, 3).map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="mt-0.5 h-6 w-6 rounded-full bg-white/10 border border-white/10 text-white text-[10px] font-black flex items-center justify-center">
                    {i + 1}
                  </div>
                  <div className="text-slate-200 text-sm leading-relaxed">{s}</div>
                </li>
              ))}
            </ul>

            <div className="mt-6 flex flex-col md:flex-row gap-3">
              {timerLeft <= 0 ? (
                <button
                  onClick={startPrescriptionTimer}
                  className="flex-1 px-6 py-4 rounded-2xl bg-smart-gold text-black text-[11px] font-black uppercase tracking-widest hover:opacity-90 transition-all"
                >
                  {isFr ? "Je fais l’exercice maintenant" : "I do the exercise now"}
                </button>
              ) : (
                <button
                  disabled
                  className="flex-1 px-6 py-4 rounded-2xl bg-white/10 border border-white/10 text-slate-300 text-[11px] font-black uppercase tracking-widest"
                >
                  {isFr ? "En cours…" : "In progress…"} {timerLeft}s
                </button>
              )}

              <button
                onClick={acceptPrescription}
                className="px-6 py-4 rounded-2xl bg-white/5 border border-white/10 text-white text-[11px] font-black uppercase tracking-widest hover:bg-white/10 transition-all"
              >
                {isFr ? "J’ai fait — Continuer" : "Done — Continue"}
              </button>

              <button
                onClick={recheckNow}
                className="px-6 py-4 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-200 text-[11px] font-black uppercase tracking-widest hover:bg-cyan-500/20 transition-all"
              >
                {isFr ? "Re-check" : "Re-check"}
              </button>
            </div>

            <div className="mt-4 text-[10px] text-slate-500">
              {isFr
                ? "Tant que cet exercice n’est pas fait, les détails avancés restent masqués. Objectif : transformer le geste, pas rassurer par la donnée."
                : "Until this exercise is done, advanced details stay hidden. Goal: transform the gesture, not reassure with data."}
            </div>
          </div>
        </div>
      </div>
    );
  }, [
    primaryPrescription,
    explanations,
    isFr,
    timerLeft,
    startPrescriptionTimer,
    acceptPrescription,
    recheckNow,
  ]);

  // -----------------------------
  // 4) Render
  // -----------------------------

  if (matchType === "ASSESSMENT") {
    return (
      <CalibrationResult
        result={rawResult}
        onAccept={() => {
          if (onHome) onHome();
        }}
      />
    );
  }

  if (!uiModel) {
    return <NeuralScanner stage="INTEGRITY_CHECK" lang={lang} />;
  }

  return (
    <div
      className={`flex flex-col h-full rounded-[48px] border overflow-hidden relative bg-[#010203]/95 backdrop-blur-3xl shadow-3xl transition-all duration-500 ${
        isSealBroken ? "border-red-500 ring-4 ring-red-500/20" : "border-white/10"
      } ${isFullscreen ? "fixed inset-0 z-[9999] rounded-none border-none" : ""}`}
    >
      {isCoachTransition && (
        <NeuralScanner
          stage="IA_CORE"
          lang={isFr ? "fr" : "en"}
          isRecalibration={true}
          labelOverride={coachTransitionLabel}
        />
      )}

      {!isFullscreen && (
        <header className="shrink-0 flex flex-col md:flex-row items-center justify-between bg-black/60 border-b border-white/10 px-8 py-4 z-50 gap-4">
          <div className="flex items-center gap-4 shrink-0">
            <button
              onClick={onHome}
              className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-[10px] font-black uppercase text-slate-400 hover:text-white transition-all"
            >
              Studio
            </button>
            <div className="h-4 w-px bg-white/10"></div>
            <button
              onClick={() => setActiveOverlay("TRUTH_PANEL")}
              className="px-4 py-2 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-[10px] font-black uppercase text-cyan-400 hover:bg-cyan-500 transition-all"
            >
              🔬 Neural Engine
            </button>
          </div>

          <nav className="flex gap-1 p-1 rounded-full border border-white/5 bg-black/40 overflow-x-auto no-scrollbar max-w-full md:max-w-[60%]">
            {visibleTabsList.map((tab) => {
              const locked = Boolean(tab.dataLocked);
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2.5 rounded-full text-[9px] font-black uppercase transition-all flex items-center gap-2 whitespace-nowrap relative shrink-0 ${
                    isActive ? "bg-white text-black shadow-lg" : "text-slate-500 hover:text-white"
                  } ${locked && !isActive ? "opacity-60" : ""}`}
                  title={
                    locked
                      ? tab.lockReason || (isFr ? "Verrouillé" : "Locked")
                      : undefined
                  }
                >
                  {tab.isPremiumOnly && isVisionLocked && (
                    <span className="text-[8px]">💎</span>
                  )}
                  {locked && tab.id !== "report" && (
                    <span className="text-[9px]">🔒</span>
                  )}
                  <span>{tab.icon}</span> {tab.label}
                </button>
              );
            })}
          </nav>

          <button
            onClick={() => setActiveOverlay("SCORING_GUIDE")}
            className="shrink-0 px-6 py-2.5 rounded-full bg-smart-gold/10 border border-smart-gold/40 text-smart-gold text-[10px] font-black uppercase tracking-widest hover:bg-smart-gold hover:text-black transition-all"
          >
            🎯 L{currentLevelId}
          </button>
        </header>
      )}

      {isSealBroken && (
        <div className="bg-red-600 text-white text-[9px] font-black uppercase py-1 text-center animate-pulse">
          ⚠️ ERREUR D&apos;INTÉGRITÉ : Les données affichées ne correspondent pas à
          la signature d&apos;origine.
        </div>
      )}

      {isIntegrityFail && (
        <div className="bg-orange-600 text-white text-[9px] font-black uppercase py-1 text-center">
          ⚠️ PERFORMANCE NON CERTIFIABLE : Échec des verrous anti-simulation.
        </div>
      )}

      {showCalibWarning && activeTab === "report" && (
        <div className="bg-smart-gold/20 text-smart-gold text-[9px] font-black uppercase py-1 text-center border-b border-smart-gold/10">
          ℹ️ Analyse indicative : Précision temporelle non calibrée.
        </div>
      )}

      <main className={`flex-1 overflow-y-auto custom-scrollbar ${isFullscreen ? "p-0" : "p-8"}`}>
        {activeTab === "report" ? (
          <InitialReport
            result={uiModel}
            onReplay={onHome || (() => {})}
            onCompare={(tab) => setActiveTab(tab || "report")}
          />
        ) : (
          <PremiumPaywall
            featureName={
              visibleTabsList.find((t) => t.id === activeTab)?.label ||
              "Analyse Avancée"
            }
            description="Cette fonctionnalité est réservée aux abonnés Studio Master."
          >
            <TabRouter
              activeTab={activeTab}
              result={uiModel}
              currentLevelId={currentLevelId}
              lang={isFr ? "fr" : "en"}
              diagnostics={uiModel.feedback?.diagnostics || []}
              isFullscreen={isFullscreen}
              onToggleFullscreen={() => setIsFullscreen((v) => !v)}
              activeMeasure={activeMeasure}
              onMeasureSelect={(m) => setActiveMeasure(m)}
              beatsPerBar={4}
              isVisionAnalyzing={false}
            />
          </PremiumPaywall>
        )}
      </main>

      <AIOverlay isLoading={false} isSpeaking={false} isFr={isFr} />

      {isForcedMode && renderForcedOverlay()}
    </div>
  );
};
